import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Minus, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { PriceDisplay } from './PriceDisplay';
import { ProductImage } from './ProductImage';
import { Skeleton } from './ui/skeleton';
import { cartApi, catalogApi, type Product } from '@/lib/api';
import { useUiStore } from '@/stores/ui';
import { withClickSpan } from '@/lib/clickSpan';

export function CartDrawer() {
  const open = useUiStore((s) => s.cartDrawerOpen);
  const closeCart = useUiStore((s) => s.closeCart);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: async () => (await cartApi.get()).data,
    refetchOnWindowFocus: false,
  });

  // Hydrate items with product info (cart only stores productId+qty).
  const items = cart.data?.items ?? [];
  const productIds = items.map((i) => i.productId).join(',');
  const products = useQuery({
    queryKey: ['cart-products', productIds],
    queryFn: async () => {
      const arr = await Promise.all(items.map((i) => catalogApi.get(i.productId).then((r) => r.data).catch(() => null)));
      const map: Record<string, Product> = {};
      arr.forEach((p) => { if (p) map[p.id] = p; });
      return map;
    },
    enabled: items.length > 0,
  });

  const updateMut = useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      quantity <= 0 ? cartApi.remove(productId) : cartApi.update(productId, quantity),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });

  // Re-fetch cart on drawer open.
  useEffect(() => {
    if (open) qc.invalidateQueries({ queryKey: ['cart'] });
  }, [open, qc]);

  const total = items.reduce((sum, it) => {
    const p = products.data?.[it.productId];
    return sum + (p ? p.price_knuts * it.quantity : 0);
  }, 0);

  function setQty(productId: string, name: string, quantity: number) {
    void withClickSpan(
      'cart-update-qty',
      { 'app.product.id': productId, 'app.cart.qty.next': quantity },
      () => updateMut.mutateAsync({ productId, quantity }),
    ).then(() => {
      // intentional no-op; toast on remove only
      if (quantity <= 0) {
        // sonner already imported globally; lightweight notice
        // (kept minimal to avoid a circular import)
        console.info('removed', name);
      }
    });
  }

  function goCheckout() {
    closeCart();
    navigate('/checkout');
  }

  return (
    <Sheet open={open} onOpenChange={(v) => (v ? null : closeCart())}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Your Cart</SheetTitle>
          <SheetDescription>{items.length === 0 ? 'Empty as a Boggart\'s wardrobe.' : `${items.length} unique item${items.length === 1 ? '' : 's'}`}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
          {cart.isLoading && <Skeleton className="h-24" />}
          {!cart.isLoading && items.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-(--color-ink-muted) mb-4">Your cart is empty.</p>
              <Button asChild variant="outline" onClick={closeCart}>
                <Link to="/">Browse the Alley</Link>
              </Button>
            </div>
          )}
          {items.map((it) => {
            const p = products.data?.[it.productId];
            return (
              <div key={it.productId} className="flex gap-3 py-3 border-b last:border-b-0">
                <div className="w-16 h-16 rounded-md shrink-0 overflow-hidden" aria-hidden="true">
                  {p && (
                    <ProductImage
                      src={p.image_url || undefined}
                      fallbackSrc={`/images/${p.sku.toLowerCase()}.png`}
                      alt=""
                      category={p.category}
                      sku={p.sku}
                      className="w-full h-full object-cover rounded-md"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{p?.name ?? it.productId}</p>
                  <PriceDisplay knuts={(p?.price_knuts ?? 0) * it.quantity} className="text-sm" />
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={`Decrease quantity of ${p?.name ?? it.productId}`}
                      onClick={() => setQty(it.productId, p?.name ?? '', it.quantity - 1)}
                      className="h-7 w-7"
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span aria-live="polite" className="text-sm w-6 text-center tabular-nums">{it.quantity}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      aria-label={`Increase quantity of ${p?.name ?? it.productId}`}
                      onClick={() => setQty(it.productId, p?.name ?? '', it.quantity + 1)}
                      className="h-7 w-7"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={`Remove ${p?.name ?? it.productId} from cart`}
                      onClick={() => setQty(it.productId, p?.name ?? '', 0)}
                      className="h-7 w-7 ml-auto"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <>
            <Separator />
            <div className="flex items-center justify-between pt-4">
              <span className="font-display text-lg">Total</span>
              <PriceDisplay knuts={total} className="text-xl" />
            </div>
            <Button variant="cta" size="lg" onClick={goCheckout} className="mt-2">
              Checkout
            </Button>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
